import { Document } from '@contentful/rich-text-types';
import type { IncludesMap, Link } from './types';

/**
 * Converts camelCase contentType IDs to PascalCase __typename.
 * Examples:
 * - 'page' → 'Page'
 * - 'heroConsolidated' → 'HeroConsolidated'
 * - 'siteFooter' → 'SiteFooter'
 */
export function toPascalCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getTypenameForContentTypeId(contentTypeId: string): string {
  const overrides: Record<string, string> = {
    toggle: 'ToggleItem',
    'section-custom-query-string-parameters': 'SectionCustomQueryStringParameters',
  };
  return overrides[contentTypeId] ?? toPascalCase(contentTypeId);
}

/**
 * Checks if a value is a Link object.
 */
export function isLink(value: unknown): value is Link {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const sys = (value as Record<string, unknown>).sys;
  if (typeof sys !== 'object' || sys === null) {
    return false;
  }
  const sysObj = sys as Record<string, unknown>;
  return sysObj.type === 'Link' && (sysObj.linkType === 'Entry' || sysObj.linkType === 'Asset');
}

/**
 * Builds a flat map of all entries and assets by sys.id from the includes array.
 */
export function buildIncludesMap(includes: { Entry?: unknown[]; Asset?: unknown[] }): IncludesMap {
  const entries = new Map<string, unknown>();
  const assets = new Map<string, unknown>();

  if (includes.Entry) {
    for (const entry of includes.Entry) {
      if (typeof entry === 'object' && entry !== null) {
        const sys = (entry as Record<string, unknown>).sys;
        if (typeof sys === 'object' && sys !== null) {
          const id = (sys as Record<string, unknown>).id;
          if (typeof id === 'string') {
            entries.set(id, entry);
          }
        }
      }
    }
  }

  if (includes.Asset) {
    for (const asset of includes.Asset) {
      if (typeof asset === 'object' && asset !== null) {
        const sys = (asset as Record<string, unknown>).sys;
        if (typeof sys === 'object' && sys !== null) {
          const id = (sys as Record<string, unknown>).id;
          if (typeof id === 'string') {
            assets.set(id, asset);
          }
        }
      }
    }
  }

  return { entries, assets };
}

export function isTypenameObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const typename = (value as Record<string, unknown>).__typename;
  return typeof typename === 'string' && typename.length > 0;
}

export function hasSysId(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const sys = (value as Record<string, unknown>).sys;
  if (typeof sys !== 'object' || sys === null || Array.isArray(sys)) return false;
  return typeof (sys as Record<string, unknown>).id === 'string';
}

export function isMediaAssetLike(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.url === 'string' && typeof obj.contentType === 'string';
}

/**
 * Checks if a value is a Contentful Document (rich text).
 */
export function isDocument(value: unknown): value is Document {
  return (
    typeof value === 'object' &&
    value !== null &&
    'nodeType' in value &&
    (value as Record<string, unknown>).nodeType === 'document'
  );
}

/**
 * Checks if a value is a Contentful Asset.
 * Assets have sys.type === 'Asset' or have a fields.file structure.
 */
export function isAsset(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;

  // Check if it has sys.type === 'Asset'
  const sys = obj.sys as Record<string, unknown> | undefined;
  if (sys?.type === 'Asset') {
    return true;
  }

  // Check if it has fields.file structure (REST API asset)
  const fields = obj.fields as Record<string, unknown> | undefined;
  if (fields && 'file' in fields) {
    return true;
  }

  return false;
}

/**
 * Helper function to unwrap locale-specific values.
 * REST API returns fields like { "en-US": "value" }, we need just "value".
 */
export function unwrapLocale<T>(value: unknown): T | undefined {
  if (typeof value !== 'object' || value === null) {
    return value as T | undefined;
  }

  const obj = value as Record<string, unknown>;
  // Try common locales
  const locales = ['en-US', 'en', Object.keys(obj)[0]];

  for (const locale of locales) {
    if (locale && locale in obj) {
      return obj[locale] as T;
    }
  }

  return undefined;
}

export function isLocaleMapObject(value: Record<string, unknown>): boolean {
  if ('sys' in value || 'fields' in value) {
    return false;
  }

  return 'en-US' in value || 'en' in value;
}

export function isEntryWithSysAndSlug(
  value: Record<string, unknown>,
): value is { __typename: string; sys: { id: string }; slug: string } {
  const typename = value.__typename;
  const sys = value.sys;
  const slug = value.slug;

  if (typeof typename !== 'string' || typeof slug !== 'string') {
    return false;
  }
  if (typeof sys !== 'object' || sys === null) {
    return false;
  }
  return typeof (sys as Record<string, unknown>).id === 'string';
}

/**
 * Checks if an array contains Contentful entries (has items with sys.contentType).
 */
export function isArrayOfEntries(arr: unknown[]): boolean {
  if (arr.length === 0) return false;
  const first = arr[0];
  if (typeof first !== 'object' || first === null) return false;
  const firstObj = first as Record<string, unknown>;
  // Check if it's either a Link or a resolved Entry
  if (isLink(firstObj)) return true;
  const sys = firstObj.sys as Record<string, unknown> | undefined;
  const contentType = sys?.contentType;
  return contentType !== undefined;
}

export function hasSysIdAndSlug(value: unknown): value is { sys: { id: string }; slug: string } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  const sys = obj.sys;
  if (!sys || typeof sys !== 'object' || Array.isArray(sys)) return false;
  const sysObj = sys as Record<string, unknown>;
  return typeof sysObj.id === 'string' && typeof obj.slug === 'string';
}
